import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

interface NavChild {
  label: string;
  path: string;
}

interface NavItem {
  label: string;
  path?: string;
  children?: NavChild[];
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<Set<string>>(new Set());
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navItems: NavItem[] = [
    { label: "Home", path: "/" },
    {
      label: "Resources",
      children: [
        { label: "Red Flags Guide", path: "/checklist" },
        { label: "Country Fit", path: "/country-fit" },
        { label: "Your Protection Packages", path: "/protection-packages" },
      ],
    },
    {
      label: "About",
      children: [
        { label: "About Us", path: "/about" },
        { label: "Contact", path: "/contact" },
      ],
    },
    { label: "Blog", path: "/blog" },
  ];

  const isLinkActive = (path: string) => location.pathname === path;

  const isDropdownActive = (children: NavChild[]) =>
    children.some((child) => isLinkActive(child.path));

  const handleMouseEnter = useCallback((label: string) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenDropdown(label);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimer.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  }, []);

  const handleDropdownLinkClick = useCallback(() => {
    setOpenDropdown(null);
  }, []);

  const toggleMobileExpand = (label: string) => {
    setMobileExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileExpanded(new Set());
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-background-200/70">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-16 md:h-32">
          <Link to="/" className="flex items-center gap-2 shrink-0 hover:scale-105 transition-transform duration-200">
            <img
              src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/41ebe134962fbd62d47884c5ad9c761c.png"
              alt="Dental Safety Abroad"
              className="h-14 md:h-48 w-auto"
              style={{ filter: 'brightness(1.05)' }}
            />
          </Link>

          <div className="hidden lg:flex items-center gap-0.5 ml-4">
            {navItems.map((item) => {
              if (item.children) {
                const isActive = isDropdownActive(item.children);
                const isOpen = openDropdown === item.label;
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                        isActive
                          ? "bg-background-200 text-foreground-900 font-medium"
                          : "text-foreground-700 hover:text-foreground-900"
                      }`}
                    >
                      {item.label}
                      <span className={`w-3.5 h-3.5 flex items-center justify-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-xs"></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-white rounded-lg border border-background-200/70 shadow-sm py-1.5 animate-dropdown">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={handleDropdownLinkClick}
                            className={`block px-4 py-2.5 text-sm transition-colors whitespace-nowrap ${
                              isLinkActive(child.path)
                                ? "text-primary-600 font-medium bg-primary-50/80"
                                : "text-foreground-700 hover:text-foreground-900 hover:bg-background-100"
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path!}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap ${
                    isLinkActive(item.path!)
                      ? "bg-background-200 text-foreground-900 font-medium"
                      : "text-foreground-700 hover:text-foreground-900"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/quiz"
              className="hidden md:inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Take the Free Quiz
            </Link>
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen}
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`}></i>
            </button>
          </div>
        </nav>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200/70 px-4 py-2">
          {navItems.map((item) => {
            if (item.children) {
              const isActive = isDropdownActive(item.children);
              const expanded = mobileExpanded.has(item.label);
              return (
                <div key={item.label} className="border-b border-background-200/50">
                  <button
                    onClick={() => toggleMobileExpand(item.label)}
                    className={`w-full flex items-center justify-between py-3 text-sm cursor-pointer ${
                      isActive ? "text-primary-600 font-medium" : "text-foreground-700"
                    }`}
                  >
                    {item.label}
                    <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </button>
                  {expanded && (
                    <div className="pb-2 pl-4 space-y-0.5">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={closeMobile}
                          className={`block py-2.5 text-sm ${
                            isLinkActive(child.path)
                              ? "text-primary-600 font-medium"
                              : "text-foreground-600"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path!}
                onClick={closeMobile}
                className={`block py-3 text-sm border-b border-background-200/50 ${
                  isLinkActive(item.path!)
                    ? "text-primary-600 font-medium"
                    : "text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
          <Link
            to="/quiz"
            className="mt-4 block text-center px-4 py-3 text-sm font-medium rounded-md bg-primary-500 text-white"
            onClick={closeMobile}
          >
            Take the Free Quiz
          </Link>
        </div>
      )}
    </header>
  );
}